import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calendar, Plus, Trash2, Edit2, Users, CheckCircle, Clock } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AssessmentWindows = () => {
  const [windows, setWindows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    name: "", window_type: "baseline", start_date: "", end_date: "", description: "",
  });

  const windowTypes = [
    { value: "baseline", label: "Baseline", color: "bg-blue-100 text-blue-800" },
    { value: "mid_year", label: "Mid-Year", color: "bg-amber-100 text-amber-800" },
    { value: "summative", label: "Summative", color: "bg-green-100 text-green-800" },
    { value: "screener", label: "Screener", color: "bg-purple-100 text-purple-800" },
    { value: "assessment", label: "General", color: "bg-gray-100 text-gray-800" },
  ];

  useEffect(() => { fetchWindows(); }, []);

  const fetchWindows = async () => {
    try {
      const res = await axios.get(`${API}/assessment-windows`);
      setWindows(res.data.windows || []);
    } catch (e) { toast.error("Failed to load assessment windows"); }
    finally { setLoading(false); }
  };

  const handleSave = async () => {
    if (!form.name || !form.start_date || !form.end_date) {
      toast.error("Name, start date, and end date are required");
      return;
    }
    try {
      if (editingId) {
        await axios.put(`${API}/assessment-windows/${editingId}`, form);
        toast.success("Assessment window updated");
      } else {
        await axios.post(`${API}/assessment-windows`, { ...form, is_active: true });
        toast.success("Assessment window created");
      }
      setShowForm(false);
      setEditingId(null);
      setForm({ name: "", window_type: "baseline", start_date: "", end_date: "", description: "" });
      fetchWindows();
    } catch (e) { toast.error("Failed to save assessment window"); }
  };

  const handleEdit = (w) => {
    setForm({
      name: w.name,
      window_type: w.window_type || "assessment",
      start_date: w.start_date ? w.start_date.slice(0, 10) : "",
      end_date: w.end_date ? w.end_date.slice(0, 10) : "",
      description: w.description || "",
    });
    setEditingId(w.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this assessment window?")) return;
    try {
      await axios.delete(`${API}/assessment-windows/${id}`);
      toast.success("Deleted");
      fetchWindows();
    } catch (e) { toast.error("Failed to delete"); }
  };

  const getTypeStyle = (type) => {
    const t = windowTypes.find(wt => wt.value === type);
    return t ? t.color : "bg-gray-100 text-gray-800";
  };

  const isActive = (w) => {
    const now = new Date().toISOString();
    return w.is_active && w.start_date <= now && w.end_date >= now;
  };

  if (loading) return <div className="py-8 text-center text-gray-500">Loading...</div>;

  return (
    <div data-testid="assessment-windows-panel">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>Assessment Windows</h3>
          <p className="text-sm text-gray-500">Schedule assessment periods to track student growth over time</p>
        </div>
        <Button onClick={() => { setShowForm(!showForm); setEditingId(null); setForm({ name: "", window_type: "baseline", start_date: "", end_date: "", description: "" }); }} className="btn-sharp-primary" data-testid="add-window-btn">
          <Plus size={16} className="mr-1" /> New Window
        </Button>
      </div>

      {/* Form */}
      {showForm && (
        <div className="border-2 border-[#002FA7] p-6 mb-6 bg-blue-50/30" data-testid="window-form">
          <h4 className="font-bold mb-4">{editingId ? "Edit" : "Create"} Assessment Window</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <Label className="text-sm font-semibold mb-1 block">Name *</Label>
              <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="e.g., Fall 2026 Baseline" className="border-2 rounded-none" data-testid="window-name-input" />
            </div>
            <div>
              <Label className="text-sm font-semibold mb-1 block">Type</Label>
              <select value={form.window_type} onChange={e => setForm({...form, window_type: e.target.value})} className="w-full h-10 border-2 border-gray-300 px-3 bg-white" data-testid="window-type-select">
                {windowTypes.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-sm font-semibold mb-1 block">Start Date *</Label>
              <Input type="date" value={form.start_date} onChange={e => setForm({...form, start_date: e.target.value})} className="border-2 rounded-none" data-testid="window-start-input" />
            </div>
            <div>
              <Label className="text-sm font-semibold mb-1 block">End Date *</Label>
              <Input type="date" value={form.end_date} onChange={e => setForm({...form, end_date: e.target.value})} className="border-2 rounded-none" data-testid="window-end-input" />
            </div>
          </div>
          <div className="mb-4">
            <Label className="text-sm font-semibold mb-1 block">Description</Label>
            <Input value={form.description} onChange={e => setForm({...form, description: e.target.value})} placeholder="Optional notes about this assessment period" className="border-2 rounded-none" />
          </div>
          <div className="flex gap-3">
            <Button onClick={handleSave} className="btn-sharp-primary" data-testid="save-window-btn">
              {editingId ? "Update" : "Create"} Window
            </Button>
            <Button variant="outline" onClick={() => { setShowForm(false); setEditingId(null); }} className="btn-sharp">Cancel</Button>
          </div>
        </div>
      )}

      {/* Windows List */}
      {windows.length === 0 ? (
        <div className="border-2 border-dashed border-gray-300 p-12 text-center text-gray-500">
          <Calendar size={40} className="mx-auto mb-3 opacity-50" />
          <p className="font-semibold mb-1">No assessment windows created yet</p>
          <p className="text-sm">Create windows to schedule assessment periods and track student growth</p>
        </div>
      ) : (
        <div className="space-y-3">
          {windows.map(w => (
            <div key={w.id} className={`border-2 p-5 ${isActive(w) ? 'border-green-400 bg-green-50/30' : 'border-gray-200'}`} data-testid={`window-${w.id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-3 mb-1">
                    <h4 className="font-bold text-lg">{w.name}</h4>
                    <span className={`text-xs px-2 py-0.5 font-semibold ${getTypeStyle(w.window_type)}`}>
                      {windowTypes.find(t => t.value === w.window_type)?.label || w.window_type}
                    </span>
                    {isActive(w) && (
                      <span className="text-xs px-2 py-0.5 bg-green-600 text-white font-semibold flex items-center gap-1">
                        <CheckCircle size={12} /> ACTIVE
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-4 text-sm text-gray-600">
                    <span className="flex items-center gap-1"><Clock size={14} /> {w.start_date?.slice(0,10)} — {w.end_date?.slice(0,10)}</span>
                    <span className="flex items-center gap-1"><Users size={14} /> {w.exam_count || 0} exams</span>
                    <span>{w.scored_count || 0} scored</span>
                  </div>
                  {w.description && <p className="text-sm text-gray-500 mt-1">{w.description}</p>}
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => handleEdit(w)} className="btn-sharp h-8" data-testid={`edit-window-${w.id}`}>
                    <Edit2 size={14} />
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => handleDelete(w.id)} className="btn-sharp h-8 text-red-600 hover:bg-red-50" data-testid={`delete-window-${w.id}`}>
                    <Trash2 size={14} />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AssessmentWindows;
